/**
 * The QuickBooks card — the integration's SETTINGS, mounted in
 * Admin > Billing > QuickBooks beside the other billing configuration.
 *
 * The billing surface does not carry this card. It carries
 * QboConnectionSummary, a one-line status in the Invoices rail: the
 * person who pushes invoices must be able to see from the cockpit that
 * QuickBooks is broken, which is the first thing they need when a push
 * fails. Seeing it is not the same as configuring it, and only the
 * seeing belongs on the page where the money is.
 *
 * The capability split follows the routes exactly, and that is FOUR
 * questions, not one:
 *   view_invoice                the connection STATE, so every office seat
 *                               can see that QuickBooks is disconnected.
 *   manage_billing_integration  Disconnect. OWNER-ONLY: it revokes access
 *                               to the books, and it is the only act that
 *                               makes room for a different company.
 *   connect_billing_integration Connect and Reconnect. owner +
 *                               exec_support: the callback refuses a
 *                               consent for a different company than the
 *                               one connected, so these can only bring
 *                               the connected company back, or connect
 *                               one when none is. The seat that runs
 *                               month end brings an expired connection
 *                               back without waiting on the owner.
 *   manage_qbo_customer_links   the customer-link ceremony. owner +
 *                               exec_support, because onboarding a client
 *                               is weekly work and routing it through one
 *                               person makes that person the bottleneck
 *                               on new revenue.
 * Asking one question for all of them is what this card used to do, and
 * it is why the bookkeeper could not onboard a client.
 *
 * The card also carries the customer-link ceremony, because that is
 * where it belongs: it proposes, the operator confirms, and the confirmed
 * write carries an explicit id. Nothing here can auto-link on a name —
 * the surface has no path to a write that does not go through a
 * confirmation click on a specific customer id.
 */

import { type ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import {
  canConnectBillingIntegration,
  canManageBillingIntegration,
  canManageQboCustomerLinks,
} from '@/lib/capabilities';
import {
  companyLabel,
  qboApi,
  qboReasonSentence,
  type QboConnectionWire,
  type QboLinkProposalWire,
  type QboLinkProposalsWire,
} from '@/lib/qboApi';
import { useSupportContact } from '@/hooks/useSupportContact';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { TextAction } from '@/components/workspace/TextAction';
import { ConfirmDialog } from '@/components/workspace/ConfirmDialog';
import { QboItemMapSection } from '@/components/billing/QboItemMapSection';
import { SelectField } from '@/components/primitives/SelectField';

const INPUT = 'border border-sage-200 rounded-tight px-2 py-1 text-sm bg-paper text-ink';

export interface QuickBooksCardProps {
  /** Seam for the consent redirect, so the hop is testable. */
  onNavigate?: (url: string) => void;
}

function connectionSentence(connection: QboConnectionWire): string {
  if (connection.status === 'absent') return "QuickBooks isn't connected, so invoices can't be sent. Press Connect QuickBooks to link it.";
  // Two companies is NOT 'absent', and saying "not connected" here used
  // to offer Connect, the one move that cannot help and can make it
  // worse. The wire now names the companies (2026-08-31), so the card
  // names BOTH and points at the remedy; an older wire without the
  // companies list keeps the honest legacy sentence.
  if (connection.status === 'multiple') {
    const companies = connection.companies ?? [];
    if (companies.length >= 2) {
      const names = companies.map((c) => companyLabel(c.company_name, c.realm_id));
      const list =
        names.length === 2
          ? `${names[0]} and ${names[1]}`
          : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
      const lead = companies.length === 2 ? 'Two' : String(companies.length);
      const which = companies.length === 2 ? "one you don't use" : "ones you don't use";
      return `${lead} QuickBooks companies are connected, ${list}, so nothing will send. Disconnect the ${which}.`;
    }
    return "More than one QuickBooks company is connected, so nothing will send. Disconnect the ones you don't use.";
  }
  const who = companyLabel(connection.company_name, connection.realmId);
  if (connection.status === 'expired') {
    return `The QuickBooks link to ${who} has run out, so nothing will send. Press Reconnect QuickBooks to renew it.`;
  }
  return `Connected to ${who} in QuickBooks.`;
}

/** A proposal is OUTSTANDING unless the client already holds a link the
 *  company actually has. An exact match is outstanding too: it is a
 *  proposal, not a link, until somebody confirms it. */
function isOutstanding(p: QboLinkProposalWire): boolean {
  return p.kind !== 'already_linked';
}

function ProposalRow({
  proposal,
  clientName,
  onConfirm,
  onUnlink,
  busy,
  refusal,
  linkedAs,
}: {
  proposal: QboLinkProposalWire;
  clientName: string;
  onConfirm: (qboCustomerId: string) => void;
  onUnlink: (clientId: string) => void;
  busy: boolean;
  refusal: string | null;
  /** Set once the SERVER has confirmed this link in this ceremony. */
  linkedAs: { qboCustomerId: string; customerActive: boolean } | null;
}): ReactNode {
  const [manualId, setManualId] = useState('');
  const candidates = 'candidates' in proposal ? proposal.candidates : [];

  // The server said 'linked'. Say so, and stop offering the control that
  // would do it again — a second press on a linked client is the mistake
  // the silent version was causing.
  if (linkedAs !== null) {
    return (
      <li
        data-testid={`qbo-proposal-${proposal.client_id}`}
        className="border-b border-sage-800/[8%] py-2 space-y-1"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-ink">{clientName}</span>
          <Chip variant="affirm">Linked</Chip>
        </div>
        <p data-testid={`qbo-just-linked-${proposal.client_id}`} className="text-sm text-ink-soft">
          {`Linked to QuickBooks record ${linkedAs.qboCustomerId}.`}
          {linkedAs.customerActive
            ? ''
            : ' That record is INACTIVE in QuickBooks; invoices will post to something archived.'}
        </p>
      </li>
    );
  }

  return (
    <li
      data-testid={`qbo-proposal-${proposal.client_id}`}
      className="border-b border-sage-800/[8%] py-2 space-y-1"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-ink">{clientName}</span>
        {/* 'exact' is the only kind with something to affirm; every other
            kind is work the operator has to think about, so it stays
            neutral rather than borrowing a colour it has not earned. */}
        <Chip variant={proposal.kind === 'exact' ? 'affirm' : 'neutral'}>
          {proposal.kind === 'exact'
            ? 'One match'
            : proposal.kind === 'ambiguous'
              ? 'Several matches'
              : proposal.kind === 'contested'
                ? 'Claimed by more than one client'
                : proposal.kind === 'linked_missing'
                  ? 'Linked QuickBooks record is not in this company'
                  : 'No match'}
        </Chip>
      </div>

      {proposal.kind === 'exact' && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-ink-soft">
          <span>{`QuickBooks customer ${proposal.display_name} (${proposal.qbo_customer_id})`}</span>
          <Button
            size="sm"
            data-testid={`qbo-confirm-${proposal.client_id}`}
            onClick={() => onConfirm(proposal.qbo_customer_id)}
            loading={busy}
          >
            Link
          </Button>
        </div>
      )}

      {(proposal.kind === 'ambiguous' || proposal.kind === 'none' || proposal.kind === 'contested') && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-ink-soft">
          {candidates.length > 0 ? (
            <SelectField size="compact"
              className="w-full"
              data-testid={`qbo-candidate-${proposal.client_id}`}
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
            >
              <option value="">Choose the client</option>
              {candidates.map((c) => (
                <option key={c.qbo_customer_id} value={c.qbo_customer_id}>
                  {`${c.display_name} (${c.qbo_customer_id})${c.active ? '' : ', inactive'}`}
                </option>
              ))}
            </SelectField>
          ) : (
            <input
              className={INPUT}
              data-testid={`qbo-manual-${proposal.client_id}`}
              placeholder="QuickBooks customer number"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
            />
          )}
          <Button
            size="sm"
            data-testid={`qbo-confirm-${proposal.client_id}`}
            disabled={manualId.trim() === ''}
            onClick={() => onConfirm(manualId.trim())}
            loading={busy}
          >
            Link
          </Button>
        </div>
      )}

      {proposal.kind === 'linked_missing' && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs text-ink-soft">
            {`This client is linked to a QuickBooks customer the connected company doesn't have. Unlink it, or reconnect to the right company.`}
          </p>
          {/* The copy above has always said "unlink it". Until now there
              was nothing to press, so the only way through was curl. */}
          <TextAction
            data-testid={`qbo-unlink-${proposal.client_id}`}
            onClick={() => onUnlink(proposal.client_id)}
          >
            Unlink
          </TextAction>
        </div>
      )}

      {refusal !== null && (
        <p role="alert" className="text-xs text-pepper">
          {refusal}
        </p>
      )}
    </li>
  );
}

export function QuickBooksCard({ onNavigate }: QuickBooksCardProps = {}): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const contact = useSupportContact();
  const queryClient = useQueryClient();
  const canManage = canManageBillingIntegration(whoami);
  const canConnect = canConnectBillingIntegration(whoami);
  const canLink = canManageQboCustomerLinks(whoami);
  const [ceremonyOpen, setCeremonyOpen] = useState(false);
  const [refusals, setRefusals] = useState<Record<string, string>>({});
  /** Clients the server has CONFIRMED linked during this ceremony.
   *  NOT optimistic: every entry is an `outcome: 'linked'` the server
   *  stated, recorded because we used to throw that answer away. */
  const [confirmed, setConfirmed] = useState<
    Record<string, { qboCustomerId: string; customerActive: boolean }>
  >({});
  const [unlinkTarget, setUnlinkTarget] = useState<{ id: string; name: string } | null>(null);
  const navigate = onNavigate ?? ((url: string) => window.location.assign(url));

  const statusQuery = useQuery({
    queryKey: ['qbo', 'status', tenantSlug],
    queryFn: ({ signal }) => qboApi.status(tenantSlug, signal),
  });

  const proposalsQuery = useQuery<QboLinkProposalsWire>({
    queryKey: ['qbo', 'customer-links', tenantSlug],
    queryFn: ({ signal }) => qboApi.linkProposals(tenantSlug, signal),
    enabled: ceremonyOpen && canLink,
  });

  const connectMut = useMutation({
    mutationFn: () => qboApi.connectStart(tenantSlug),
    onSuccess: (res) => navigate(res.authorize_url),
  });

  const disconnectMut = useMutation({
    mutationFn: (realmId: string) => qboApi.disconnect(tenantSlug, realmId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['qbo', 'status', tenantSlug] });
    },
  });

  const confirmMut = useMutation({
    mutationFn: (vars: { client_id: string; qbo_customer_id: string }) =>
      qboApi.confirmLink(tenantSlug, vars),
    onSuccess: (res, vars) => {
      if (res.outcome === 'linked') {
        setRefusals((r) => {
          const next = { ...r };
          delete next[vars.client_id];
          return next;
        });
        setConfirmed((c) => ({
          ...c,
          [vars.client_id]: {
            qboCustomerId: res.qbo_customer_id,
            customerActive: res.customer_active,
          },
        }));
        // DELIBERATELY NOT invalidating (founder's network tab,
        // 2026-08-26). This GET re-reads the ENTIRE live QuickBooks
        // customer list and takes 9-16 seconds; firing it per link meant
        // each link ABORTED the previous refetch, so the list never
        // settled and every link looked like it had failed. We do not
        // need to re-read QuickBooks to learn what the confirm response
        // just told us. Closing and reopening the ceremony refetches.
        return;
      }
      setRefusals((r) => ({ ...r, [vars.client_id]: qboReasonSentence(res.reason, contact) }));
    },
    onError: (_e, vars) => {
      setRefusals((r) => ({ ...r, [vars.client_id]: qboReasonSentence('link_write_failed', contact) }));
    },
  });

  const unlinkMut = useMutation({
    mutationFn: (clientId: string) => qboApi.unlink(tenantSlug, clientId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['qbo', 'customer-links', tenantSlug] });
    },
  });

  const connection: QboConnectionWire = statusQuery.data?.connection ?? { status: 'absent' };
  const proposals = proposalsQuery.data;
  const outstanding =
    proposals?.outcome === 'proposed' ? proposals.proposals.filter(isOutstanding) : [];
  // The other half of the ceremony, and the half that did not exist: a
  // client whose link is GOOD never appeared here, so a wrong link had
  // no surface to undo it from and the only route was curl.
  const linked =
    proposals?.outcome === 'proposed'
      ? proposals.proposals.filter((p) => p.kind === 'already_linked')
      : [];
  const clientNames = new Map(
    (proposals?.outcome === 'proposed' ? (proposals.clients ?? []) : []).map((c) => [
      c.id,
      c.display_name,
    ]),
  );

  return (
    <section
      data-testid="qbo-card"
      className="space-y-2 border border-sage-200 bg-sage-50 rounded-card p-4 sm:p-5"
    >
      <h2 className="font-display text-lg text-sage-800">QuickBooks</h2>

      <p data-testid="qbo-connection-state" className="text-sm text-ink-soft">
        {statusQuery.isPending ? 'Checking the connection…' : connectionSentence(connection)}
      </p>

      {/* The realm id DEMOTES out of the sentence (2026-08-31) but must
          stay reachable: it is what an Intuit support ticket asks for. */}
      {(connection.status === 'connected' || connection.status === 'expired') && (
        <details className="text-xs text-ink-soft">
          <summary className="cursor-pointer">Connection details</summary>
          <p data-testid="qbo-connection-details" className="pt-1">
            {`QuickBooks company id ${connection.realmId}.`}
          </p>
        </details>
      )}

      {(canManage || canConnect || canLink) && (
        <div className="flex flex-wrap items-center gap-3">
          {connection.status === 'absent' ? (
            canConnect && (
              <Button
                data-testid="qbo-connect"
                onClick={() => connectMut.mutate()}
                loading={connectMut.isPending}
              >
                Connect QuickBooks
              </Button>
            )
          ) : connection.status === 'multiple' ? (
            // The worst connection state keeps its recovery surface, and
            // (2026-08-31) finally gets its actual remedy: the wire's
            // companies list names each realm, so Disconnect is
            // PER-COMPANY and explicit — no guessing, which is what the
            // store refused all along. Still no Connect: a third
            // company helps nobody. The item map keeps reading and
            // reporting on its own terms.
            <>
              {canManage &&
                (connection.companies ?? []).map((c) => (
                  <TextAction
                    key={c.realm_id}
                    data-testid={`qbo-disconnect-${c.realm_id}`}
                    onClick={() => disconnectMut.mutate(c.realm_id)}
                  >
                    {`Disconnect ${companyLabel(c.company_name, c.realm_id)}`}
                  </TextAction>
                ))}
              {canLink && <QboItemMapSection slug={tenantSlug} />}
            </>
          ) : (
            <>
              {/* A reconnect IS a connect: same start route, and the
                  callback's same-realm re-auth path welcomes it (a
                  DIFFERENT company is refused there, fact-shaped). That
                  refusal is why the seat that may not disconnect may
                  still do this. */}
              {canConnect && connection.status === 'expired' && (
                <Button
                  data-testid="qbo-reconnect"
                  onClick={() => connectMut.mutate()}
                  loading={connectMut.isPending}
                >
                  Reconnect QuickBooks
                </Button>
              )}
              {canLink && (
                <TextAction
                  data-testid="qbo-link-ceremony-open"
                  onClick={() => setCeremonyOpen((v) => !v)}
                >
                  {ceremonyOpen ? 'Hide customer links' : 'Match clients to QuickBooks customers'}
                </TextAction>
              )}
              {canLink && <QboItemMapSection slug={tenantSlug} />}
              {canManage && (
                <TextAction
                  data-testid="qbo-disconnect"
                  onClick={() => disconnectMut.mutate(connection.realmId)}
                >
                  Disconnect
                </TextAction>
              )}
            </>
          )}
        </div>
      )}

      {ceremonyOpen && canLink && (
        <div className="pt-2">
          {proposalsQuery.isPending && <p className="text-sm text-ink-soft">Reading QuickBooks…</p>}

          {proposals?.outcome === 'unavailable' && (
            <p role="alert" className="text-sm text-pepper">
              {qboReasonSentence(proposals.reason, contact)}
            </p>
          )}

          {proposals?.outcome === 'proposed' && (
            <>
              <p className="text-sm text-ink">
                <span data-testid="qbo-unlinked-count" className="font-semibold">
                  {outstanding.filter((p) => confirmed[p.client_id] === undefined).length}
                </span>
                {outstanding.filter((p) => confirmed[p.client_id] === undefined).length === 1
                  ? ' client to match to a QuickBooks customer.'
                  : ' clients to match to a QuickBooks customer.'}
              </p>
              <p className="text-xs text-ink-soft">
                Nothing links itself. A name that looks right is a suggestion; the link happens when
                you press Link.
              </p>
              <ul className="mt-2">
                {outstanding.map((p) => (
                  <ProposalRow
                    key={p.client_id}
                    proposal={p}
                    clientName={clientNames.get(p.client_id) ?? p.client_id}
                    busy={
                      confirmMut.isPending && confirmMut.variables?.client_id === p.client_id
                    }
                    refusal={refusals[p.client_id] ?? null}
                    linkedAs={confirmed[p.client_id] ?? null}
                    onConfirm={(qboCustomerId) =>
                      confirmMut.mutate({ client_id: p.client_id, qbo_customer_id: qboCustomerId })
                    }
                    onUnlink={(clientId) =>
                      setUnlinkTarget({
                        id: clientId,
                        name: clientNames.get(clientId) ?? clientId,
                      })
                    }
                  />
                ))}
              </ul>

              {linked.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-sage-800">
                    {linked.length === 1
                      ? '1 client is linked'
                      : `${linked.length} clients are linked`}
                  </h4>
                  <ul className="mt-1">
                    {linked.map((p) => (
                      <li
                        key={p.client_id}
                        data-testid={`qbo-linked-${p.client_id}`}
                        className="flex flex-wrap items-center gap-2 border-b border-sage-800/[8%] py-2 text-sm"
                      >
                        <span className="font-medium text-ink">
                          {clientNames.get(p.client_id) ?? p.client_id}
                        </span>
                        <span className="text-ink-soft">
                          {'qbo_customer_id' in p
                            ? `QuickBooks customer ${p.qbo_customer_id}`
                            : ''}
                        </span>
                        <TextAction
                          data-testid={`qbo-unlink-${p.client_id}`}
                          onClick={() =>
                            setUnlinkTarget({
                              id: p.client_id,
                              name: clientNames.get(p.client_id) ?? p.client_id,
                            })
                          }
                        >
                          Unlink
                        </TextAction>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {unlinkTarget && (
        <ConfirmDialog
          title="Unlink this client from QuickBooks?"
          body={`Invoices for ${unlinkTarget.name} will stop sending to QuickBooks until you link the client again. Nothing already in QuickBooks changes, and nothing here is deleted.`}
          confirmLabel="Unlink"
          destructive
          onConfirm={() => {
            const id = unlinkTarget.id;
            setUnlinkTarget(null);
            unlinkMut.mutate(id);
          }}
          onCancel={() => setUnlinkTarget(null)}
        />
      )}
    </section>
  );
}
